/**
 * The Odżywianie tab's glyphs: the four macro marks and the day-status marks.
 *
 * The macro marks are Phosphor Icons' fill set (MIT, phosphoricons.com) — fire, fish, grains and
 * drop — the same four the approved mockup and the phone screens draw. The status marks are the
 * mockup's: a disc with a tick, a rounded diamond with "!", a hollow ring, and a ring filling with
 * the share of today's energy eaten so far. Shape carries the status as well as colour.
 */

import type { Nutrient } from '../model/nutrition';
import type { DayStatusKind } from '../model/nutritionWeeks';

import styles from './NutritionTab.module.css';

const MACRO_PATHS: Readonly<Record<Nutrient, string>> = {
  energy:
    'M143.38,17.85a8,8,0,0,0-12.63,3.41l-22,60.41L84.59,58.26a8,8,0,0,0-11.93.89C51,87.53,40,116.08,40,144a88,88,0,0,0,176,0C216,84.55,165.21,36,143.38,17.85Zm40.51,135.49a57.6,57.6,0,0,1-46.56,46.55A7.65,7.65,0,0,1,136,200a8,8,0,0,1-1.32-15.89c16.57-2.79,30.63-16.85,33.44-33.45a8,8,0,0,1,15.78,2.68Z',
  protein:
    'M168,76a12,12,0,1,1-12-12A12,12,0,0,1,168,76Zm48.72,67.64c-19.37,34.9-55.44,53.76-107.24,56.1l-22,51.41A8,8,0,0,1,80.1,256l-.51,0a8,8,0,0,1-7.19-5.78L57.6,198.39,5.8,183.56a8,8,0,0,1-1-15.05l51.41-22c2.35-51.78,21.21-87.84,56.09-107.22,24.75-13.74,52.74-15.84,71.88-15.18,18.64.64,36,4.27,38.86,6a8,8,0,0,1,2.83,2.83c1.69,2.85,5.33,20.21,6,38.85C232.55,90.89,230.46,118.89,216.72,143.64Zm-4.3-100.07c-14.15-3-64.1-11-100.3,14.75a81.21,81.21,0,0,0-16,15.07,36,36,0,0,0,39.35,38.44,8,8,0,0,1,8.73,8.73,36,36,0,0,0,38.47,39.34,80.81,80.81,0,0,0,15-16C223.42,107.73,215.42,57.74,212.42,43.57Z',
  carbs:
    'M208,56a87.52,87.52,0,0,0-31.84,6c-14.32-29.7-43.25-44.46-44.57-45.13a8,8,0,0,0-7.16,0C123.1,17.51,94.17,32.27,79.85,62A87.52,87.52,0,0,0,48,56a8,8,0,0,0-8,8v80a88.12,88.12,0,0,0,75.48,87.1,4,4,0,0,0,4.52-4V176.27a8.18,8.18,0,0,1,7.47-8.25,8,8,0,0,1,8.53,8v51.14a4,4,0,0,0,4.52,4A88.12,88.12,0,0,0,216,144V64A8,8,0,0,0,208,56Zm-88,93.46a88,88,0,0,0-64-37.09V72.44A72.1,72.1,0,0,1,120,144Zm8-42.1A88.61,88.61,0,0,0,94.16,69.11c9.21-19.21,26.4-31.33,33.84-35.9,7.45,4.58,24.63,16.7,33.84,35.9A88.61,88.61,0,0,0,128,107.36Zm72,5a88,88,0,0,0-64,37.09V144a72.1,72.1,0,0,1,64-71.56Z',
  fat: 'M174,47.75a254.19,254.19,0,0,0-41.45-38.3,8,8,0,0,0-9.18,0A254.19,254.19,0,0,0,82,47.75C54.51,79.32,40,112.6,40,144a88,88,0,0,0,176,0C216,112.6,201.49,79.32,174,47.75Zm9.85,105.59a57.6,57.6,0,0,1-46.56,46.55A8.75,8.75,0,0,1,136,200a8,8,0,0,1-1.32-15.89c16.57-2.79,30.63-16.85,33.44-33.45a8,8,0,0,1,15.78,2.68Z',
};

const MACRO_CLASS: Readonly<Record<Nutrient, string | undefined>> = {
  energy: styles.macroEnergy,
  protein: styles.macroProtein,
  carbs: styles.macroCarbs,
  fat: styles.macroFat,
};

export function MacroGlyph({
  nutrient,
  small,
}: {
  readonly nutrient: Nutrient;
  readonly small?: boolean;
}) {
  return (
    <svg
      aria-hidden="true"
      className={`${String(styles.macro)} ${small === true ? String(styles.macroSm) : ''} ${String(MACRO_CLASS[nutrient])}`}
      fill="currentColor"
      viewBox="0 0 256 256"
    >
      <path d={MACRO_PATHS[nutrient]} />
    </svg>
  );
}

const LINE = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** The mockup's compare mark: two columns, each with its own stub. */
export function CompareGlyph() {
  return (
    <svg {...LINE}>
      <path d="M8 3v18M16 3v18M3 8h5M16 16h5" />
    </svg>
  );
}

/** The mockup's calculator on „Zmień cel" / „Ustaw cel". */
export function CalculatorGlyph() {
  return (
    <svg {...LINE}>
      <rect height="18" rx="2" width="14" x="5" y="3" />
      <path d="M8 7h8M8 12h.01M12 12h.01M16 12h.01M8 16h.01M12 16h.01M16 16h.01" />
    </svg>
  );
}

/** A day's status as a shape. `share` fills the in-progress ring, 0–1. */
export function StatusGlyph({ kind, share }: { readonly kind: DayStatusKind; readonly share: number }) {
  if (kind === 'unjudged') return null;
  return (
    <svg aria-hidden="true" className={styles.statusGlyph} viewBox="0 0 24 24">
      {kind === 'ok' ? (
        <>
          <circle className={styles.statusOk} cx="12" cy="12" r="10" />
          <path className={styles.statusOkMark} d="M7.5 12.4l3 3 6-6.2" />
        </>
      ) : kind === 'off' ? (
        <>
          <rect
            className={styles.statusOff}
            height="15.6"
            rx="4"
            transform="rotate(45 12 12)"
            width="15.6"
            x="4.2"
            y="4.2"
          />
          <path className={styles.statusOffMark} d="M12 7.6v5.4" />
          <circle className={styles.statusOffDot} cx="12" cy="16.4" r="1.5" />
        </>
      ) : kind === 'none' ? (
        <circle className={styles.statusNone} cx="12" cy="12" r="9" />
      ) : (
        <>
          <circle className={styles.statusTrack} cx="12" cy="12" r="9" />
          {share > 0 ? <path className={styles.statusWedge} d={wedge(share)} /> : null}
        </>
      )}
    </svg>
  );
}

/** A pie wedge from twelve o'clock, radius 7, covering `share` of the turn. */
function wedge(share: number): string {
  const angle = Math.min(0.999, share) * 2 * Math.PI;
  const x = 12 + 7 * Math.sin(angle);
  const y = 12 - 7 * Math.cos(angle);
  return `M12 12V5A7 7 0 ${angle > Math.PI ? '1' : '0'} 1 ${x.toFixed(2)} ${y.toFixed(2)}z`;
}
